
import {
  defineComponent,
  reactive,
  toRefs,
  onMounted,
  computed,
  Ref
} from "vue";
import { useAuth, PermissionPayload } from "../../modules/auth";
import { useToast, useConfirm } from "../../modules/message";
import {
  OrcamentoPayload,
  OrcamentoItemPayload,
  useOrcamentoService
} from "../../modules/business/orcamento/orcamentoBusiness";
import { useOrgaoAetService } from "../../modules/business/common/commonBusiness";
import { useTipoLicencaOrgaoService } from "../../modules/business/licenca/tipoLicencaOrgaoBusiness";
import { TransportadorPayload } from "../../modules/business/transportador/transportadorBusiness";
import EntitySelectorTransportador from "../transportador/EntitySelectorTransportador.vue";
import ModalEnvioWhatsApp from "./ModalEnvioWhatsApp.vue";
import UploadDocumentosOrcamento from "./UploadDocumentosOrcamento.vue";
import PreviewLicencasOrcamento from "./PreviewLicencasOrcamento.vue";
import StatusRenovacaoOrcamento from "./StatusRenovacaoOrcamento.vue";
import CrudLicenca from "../licenca/CrudLicenca.vue";

interface OrcamentoCadastroState {
  entity: OrcamentoPayload;
  stateReady: boolean;
  savedEntity: boolean;
  stateId: string;
  loadings: Array<Ref<boolean>>;
  showEditarPreco: boolean;
  itemEdicao: OrcamentoItemPayload | null;
  honorarioEdicao: number | null;
  showWhatsApp: boolean;
  showAdicionarItem: boolean;
  showBuscarLicenca: boolean;
  novoItem: OrcamentoItemPayload;
}

const emptyNovoItem = (): OrcamentoItemPayload => ({
  placa: null,
  idOrgao: null,
  tipoSolicitacao: null,
  honorario: null
});

const FONTES: Record<string, string> = {
  CON: "Contrato",
  TAB: "Tabela",
  MAN: "Manual",
  FAL: "Fallback"
};

export default defineComponent({
  setup(props, { emit }) {
    const orcamentoService = useOrcamentoService();
    const state = reactive<OrcamentoCadastroState>({
      entity: orcamentoService.safeOrcamento(),
      savedEntity: false,
      stateReady: false,
      stateId:
        "state-" +
        Math.random()
          .toString(36)
          .substring(2),
      loadings: [],
      showEditarPreco: false,
      itemEdicao: null,
      honorarioEdicao: null,
      showWhatsApp: false,
      showAdicionarItem: false,
      showBuscarLicenca: false,
      novoItem: emptyNovoItem()
    });

    const amIAllowed = (
      authority: string,
      permissionInfo?: PermissionPayload | string | null
    ) => useAuth().amIAllowed(authority, permissionInfo);

    const stateLoading = computed(
      () => state.loadings.length !== 0 || props.parentLoading
    );

    const stateBlocked = computed(
      () =>
        !!(
          stateLoading.value ||
          (state.entity.idOrcamento &&
            !amIAllowed("edit", state.entity.permissionInfo))
        )
    );

    const origens = orcamentoService.getOrigens();

    const orgaosAet = computed(() =>
      useOrgaoAetService()
        .getOrgaosAet()
        .filter((o) => o.idOrgao != null)
    );

    const tiposSolicitacao = computed(() =>
      useTipoLicencaOrgaoService()
        .getTiposLicencaOrgao()
        .filter((t) => t.idTipoLicencaOrgao != null)
    );

    const estagioPermiteEditarItem = computed(() => {
      return ["NOV", "ORC"].includes(state.entity.estagio ?? "");
    });

    const resultsItens = computed(() => ({
      content: state.entity.itens ?? [],
      page: {
        size: state.entity.itens?.length ?? 0,
        number: 0,
        totalElements: state.entity.itens?.length ?? 0,
        totalPages: 1
      }
    }));

    const transportadorSelecionado = computed<TransportadorPayload | undefined>(
      () => {
        if (!state.entity.idTransportador) {
          return undefined;
        }
        return {
          idTransportador: state.entity.idTransportador,
          nome: state.entity.nomeTransportador ?? null,
          cpfCnpj: state.entity.cpfCnpjTransportador ?? null,
          municipio: {},
          contratoSocial: {},
          procuracao: {},
          usuario: {},
          transportadorOrgaos: []
        } as TransportadorPayload;
      }
    );

    const setEntity = (entity: OrcamentoPayload) => {
      state.entity = orcamentoService.safeOrcamento(entity);
    };

    const closeScreen = () => {
      emit(
        "after-register-screen-closed",
        state.savedEntity ? state.entity : undefined
      );
    };

    const confirmSelectionTransportador = (t: TransportadorPayload | null) => {
      if (t && t.idTransportador) {
        state.entity.idTransportador = t.idTransportador;
        state.entity.nomeTransportador = t.nome ?? null;
        state.entity.cpfCnpjTransportador = t.cpfCnpj ?? null;
      } else {
        state.entity.idTransportador = null;
        state.entity.nomeTransportador = null;
        state.entity.cpfCnpjTransportador = null;
      }
    };

    const getNomeOrgao = (idOrgao?: number | null) =>
      useOrgaoAetService().getOrgaoAetStr(idOrgao ?? undefined);

    const getDescricaoTipoSolicitacao = (
      idTipoLicencaOrgao?: number | null
    ) => {
      if (idTipoLicencaOrgao === null || idTipoLicencaOrgao === undefined)
        return "";
      return (
        useTipoLicencaOrgaoService().getTipoLicencaOrgao(idTipoLicencaOrgao)
          ?.descricao ?? ""
      );
    };

    const getDescricaoFonte = (codigo?: string | null) =>
      codigo ? FONTES[codigo] ?? codigo : "";

    const getNomeEstagio = (codigo?: string | null) => {
      const e = orcamentoService.getEstagio(codigo);
      return e?.nome ?? codigo ?? "";
    };

    const formatMoney = (valor?: number | null) => {
      if (valor === null || valor === undefined) return "R$ 0,00";
      return valor.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL"
      });
    };

    const podeTransicao = (destino: string) => {
      return orcamentoService.isTransicaoPermitida(
        state.entity.estagio,
        destino
      );
    };

    const submitSave = () => {
      if (state.entity.idOrcamento) {
        return;
      }
      const { saveNewOrcamento, loading, data } = useOrcamentoService();
      state.loadings.push(loading);
      saveNewOrcamento(state.entity)
        .then(() => {
          setEntity(data.value);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.savedEntity = true;
          useToast().success("Orçamento salvo com sucesso");
          emit("after-save-new-entity", data.value.idOrcamento);
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const aplicarTransicao = (destino: string, observacoes?: string) => {
      if (!state.entity.idOrcamento) return;
      const { updateEstagio, loading, data } = useOrcamentoService();
      state.loadings.push(loading);
      updateEstagio(state.entity.idOrcamento, {
        estagio: destino,
        observacoes
      })
        .then(() => {
          setEntity(data.value);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
          state.savedEntity = true;
          useToast().success(
            `Orçamento movido para ${getNomeEstagio(destino)}`
          );
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const transicionar = (destino: string) => {
      aplicarTransicao(destino);
    };

    const iniciarRenovacao = () => {
      if (!state.entity.idOrcamento) return;
      useConfirm().require({
        message:
          "Para começar a renovação, vamos verificar se o transportador já tem uma ART válida cadastrada. Deseja iniciar a renovação agora?",
        header: "Iniciar renovação das AETs",
        icon: "pi pi-info-circle",
        acceptLabel: "Sim, iniciar",
        rejectLabel: "Cancelar",
        accept: () => {
          const {
            iniciarRenovacao: doRenovar,
            loading,
            data
          } = useOrcamentoService();
          state.loadings.push(loading);
          doRenovar(state.entity.idOrcamento as number)
            .then(() => {
              setEntity(data.value);
              state.loadings.splice(state.loadings.indexOf(loading), 1);
              state.savedEntity = true;
              const novoEstagio = (data.value as OrcamentoPayload).estagio;
              if (novoEstagio === "SAR") {
                useToast().info(
                  "Nenhuma ART vigente encontrada para este transportador. Envie o arquivo da ART para continuar."
                );
              } else {
                useToast().success(
                  "ART vigente localizada. Pode revisar as licenças e gerar as AETs."
                );
              }
            })
            .catch(() => {
              state.loadings.splice(state.loadings.indexOf(loading), 1);
            });
        }
      });
    };

    const onAfterRenovacaoStep = (orcamentoAtualizado: OrcamentoPayload) => {
      if (orcamentoAtualizado) {
        setEntity(orcamentoAtualizado);
        state.savedEntity = true;
      }
    };

    const reativar = () => {
      useConfirm().require({
        message:
          "Reativar irá mover o orçamento de volta para o estágio ORÇANDO.",
        header: "Reativar orçamento",
        icon: "pi pi-info-circle",
        accept: () => aplicarTransicao("ORC")
      });
    };

    const emitirManualmente = () => {
      useConfirm().require({
        message:
          "Marcar este orçamento como já emitido fora do sistema? Ele será arquivado e não passará pelo pipeline de robôs. Esta ação não pode ser desfeita.",
        header: "Emitir manualmente",
        icon: "pi pi-check-square",
        acceptLabel: "Sim, emitir",
        rejectLabel: "Cancelar",
        accept: () => aplicarTransicao("EMM")
      });
    };

    const recarregar = () => {
      if (!state.entity.idOrcamento) return;
      const { getOrcamento, loading, data } = useOrcamentoService();
      state.loadings.push(loading);
      getOrcamento(state.entity.idOrcamento)
        .then(() => {
          setEntity(data.value);
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        })
        .catch(() => {
          state.loadings.splice(state.loadings.indexOf(loading), 1);
        });
    };

    const abrirAdicionarItem = () => {
      state.novoItem = emptyNovoItem();
      state.showAdicionarItem = true;
    };

    const abrirBuscarLicenca = () => {
      state.showBuscarLicenca = true;
    };

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const onSelectLicenca = (selected: any) => {
      const lic = Array.isArray(selected) ? selected[0] : selected;
      if (lic) {
        state.novoItem.idLicenca = lic.idLicenca ?? null;
        const placa =
          lic.veiculoTratorPrincipal?.placa ??
          lic.placa ??
          lic.licenca?.veiculoTratorPrincipal?.placa ??
          null;
        if (placa) {
          state.novoItem.placa = placa;
        }
      }
      state.showBuscarLicenca = false;
    };
    /* eslint-enable @typescript-eslint/no-explicit-any */

    const salvarNovoItem = () => {
      if (
        !state.novoItem.placa ||
        !state.novoItem.idOrgao ||
        !state.novoItem.tipoSolicitacao
      ) {
        useToast().warning("Preencha placa, órgão e tipo de solicitação");
        return;
      }

      // Modo edicao: orcamento ja existe -> POST /itens
      if (state.entity.idOrcamento) {
        const service = useOrcamentoService();
        state.loadings.push(service.loading);
        service
          .adicionarItem(state.entity.idOrcamento, state.novoItem)
          .then(() => {
            state.loadings.splice(state.loadings.indexOf(service.loading), 1);
            state.showAdicionarItem = false;
            state.savedEntity = true;
            useToast().success("Item adicionado ao orçamento");
            recarregar();
          })
          .catch(() => {
            state.loadings.splice(state.loadings.indexOf(service.loading), 1);
          });
        return;
      }

      // Modo novo: ainda nao tem id -> empilha local, sera enviado no Salvar
      if (!Array.isArray(state.entity.itens)) {
        state.entity.itens = [];
      }
      state.entity.itens.push({ ...state.novoItem });
      state.showAdicionarItem = false;
      useToast().success(
        "Item adicionado. Clique em Salvar para gravar o orçamento."
      );
    };

    const abrirEditarPreco = (item: OrcamentoItemPayload) => {
      state.itemEdicao = item;
      state.honorarioEdicao = item.honorario ?? 0;
      state.showEditarPreco = true;
    };

    const salvarPrecoItem = () => {
      if (!state.itemEdicao || state.honorarioEdicao === null) {
        return;
      }

      // Modo edicao: item ja persistido -> PATCH preco
      if (state.entity.idOrcamento && state.itemEdicao.idOrcamentoItem) {
        const { atualizarPrecoItem, loading } = useOrcamentoService();
        state.loadings.push(loading);
        atualizarPrecoItem(
          state.entity.idOrcamento,
          state.itemEdicao.idOrcamentoItem,
          { honorario: state.honorarioEdicao }
        )
          .then(() => {
            recarregar();
            state.loadings.splice(state.loadings.indexOf(loading), 1);
            state.showEditarPreco = false;
            useToast().success("Preço atualizado");
          })
          .catch(() => {
            state.loadings.splice(state.loadings.indexOf(loading), 1);
          });
        return;
      }

      // Modo novo: atualiza in-memory
      state.itemEdicao.honorario = state.honorarioEdicao;
      state.itemEdicao.fontePrecificacao = "MAN";
      state.showEditarPreco = false;
      useToast().success("Preço atualizado");
    };

    const removerItem = (item: OrcamentoItemPayload) => {
      useConfirm().require({
        message: `Remover o item da placa ${item.placa ?? ""}?`,
        header: "Atenção",
        icon: "pi pi-info-circle",
        acceptClass: "p-button-danger",
        accept: () => {
          // Modo edicao: item ja persistido -> DELETE
          if (state.entity.idOrcamento && item.idOrcamentoItem) {
            const service = useOrcamentoService();
            state.loadings.push(service.loading);
            service
              .removerItem(
                state.entity.idOrcamento as number,
                item.idOrcamentoItem as number
              )
              .then(() => {
                recarregar();
                state.loadings.splice(
                  state.loadings.indexOf(service.loading),
                  1
                );
                state.savedEntity = true;
                useToast().success("Item removido");
              })
              .catch(() => {
                state.loadings.splice(
                  state.loadings.indexOf(service.loading),
                  1
                );
              });
            return;
          }

          // Modo novo: remove in-memory
          const idx = (state.entity.itens ?? []).indexOf(item);
          if (idx >= 0) {
            state.entity.itens.splice(idx, 1);
            useToast().success("Item removido");
          }
        }
      });
    };

    const baixarPdf = () => {
      if (!state.entity.idOrcamento) return;
      useOrcamentoService().downloadPdf(state.entity.idOrcamento);
    };

    const abrirWhatsApp = () => {
      if (!state.entity.idOrcamento) return;
      state.showWhatsApp = true;
    };

    onMounted(async () => {
      if (props.idOrcamento) {
        const { getOrcamento, loading, data } = useOrcamentoService();
        state.loadings.push(loading);
        getOrcamento(props.idOrcamento)
          .then(() => {
            setEntity(data.value);
            state.loadings.splice(state.loadings.indexOf(loading), 1);
            state.stateReady = true;
          })
          .catch(() => {
            state.loadings.splice(state.loadings.indexOf(loading), 1);
            closeScreen();
          });
      } else {
        state.entity.estagio = "NOV";
        state.entity.origem = "MAN";
        state.stateReady = true;
      }
    });

    return {
      submitSave,
      closeScreen,
      amIAllowed,
      confirmSelectionTransportador,
      origens,
      estagioPermiteEditarItem,
      resultsItens,
      transportadorSelecionado,
      podeTransicao,
      transicionar,
      iniciarRenovacao,
      emitirManualmente,
      onAfterRenovacaoStep,
      reativar,
      abrirEditarPreco,
      salvarPrecoItem,
      abrirAdicionarItem,
      salvarNovoItem,
      abrirBuscarLicenca,
      onSelectLicenca,
      orgaosAet,
      tiposSolicitacao,
      removerItem,
      baixarPdf,
      abrirWhatsApp,
      getNomeOrgao,
      getDescricaoTipoSolicitacao,
      getDescricaoFonte,
      getNomeEstagio,
      formatMoney,
      stateLoading,
      stateBlocked,
      ...toRefs(state)
    };
  },
  components: {
    EntitySelectorTransportador,
    ModalEnvioWhatsApp,
    UploadDocumentosOrcamento,
    PreviewLicencasOrcamento,
    StatusRenovacaoOrcamento,
    CrudLicenca
  },
  props: {
    idOrcamento: Number,
    parentLoading: Boolean
  }
});
